import { paymentCollectionContextProps } from './payment-collection-tracking'
import type { PaymentCollectionContext } from './payment-collection-tracking'

export type FieldGroup = 'customer' | 'items' | 'shipping' | 'payment'
export type FormVariant = 'desktop' | 'mobile'
export type WorkflowOutcome = 'api_confirmed' | 'closed' | 'navigated_away'
export type WorkflowAction =
    | 'api_failed'
    | 'validation_failed'
    | 'customer_selected'
    | 'product_selected'
    | 'lot_selected'
    | 'item_added'
    | 'item_removed'
    | 'retry'
    | 'confirm_requested'
    | 'confirm_failed'
export interface WorkflowIdentity {
    actorId: string
    impersonating: boolean
    identityKey: string
}
export interface WorkflowContext extends PaymentCollectionContext {
    lineCount?: number
    customerLookupState?: 'unselected' | 'prefilled' | 'selected'
    entryMethod?: 'manual' | 'barcode'
    /** Số vùng trồng đã chọn trên phiếu cân tươi (M-27). */
    areaCount?: number
    /** Số dòng "mua từ hộ kinh doanh" khi tạo nhiều phiếu cân khô (M-27). */
    businessLineCount?: number
    /**
     * Cột được sửa tại chỗ trên danh sách phiếu cân tươi (M-31).
     *
     * `tea_purchase_update` dùng chung cho mọi lần sửa phiếu, nên thiếu
     * dimension này thì không tách được "điền giống chè cho phiếu đồng bộ" ra
     * khỏi các lần sửa khác.
     */
    editedField?: 'tea_pattern' | 'tea_type_id'
    /** Nguồn phiếu đang sửa: nhân viên nhập tay hay đồng bộ SmartLab (M-31). */
    purchaseSource?: 'manual' | 'smartlab'
    /** Ô đã có giá trị từ trước hay đang trống (M-31). */
    hadValue?: boolean
    /**
     * Nguồn khoản chi đang sửa (M-75): nhập tay hay sinh từ sao kê ngân hàng.
     * Popup khoản chi từ sao kê hiện nội dung chuyển khoản; thiếu dimension
     * này thì `expense_update` không tách được hai loại.
     */
    expenseSource?: 'manual' | 'bank_statement'
    /**
     * Loại đơn đang xuất kho (M-76): đơn quầy "Bán ngay" giờ chờ thủ kho bấm
     * "Xuất kho" như đơn KiotViet. Thiếu dimension này thì `order_stock_out`
     * không tách được lần xuất đơn quầy khỏi đơn giao hàng thường.
     */
    orderSource?: 'counter' | 'kiotviet' | 'standard'
    /**
     * Đơn đang ở đâu lúc phát hành HĐĐT tại quầy (M-132): "Bán ngay" giờ phát
     * hành được ngay khi chốt, trước khi thủ kho xuất kho. Thiếu dimension này
     * thì `pos_einvoice_push` không tách được hóa đơn phát hành sớm.
     */
    orderStage?: 'awaiting_stock_out' | 'delivered'
    /**
     * Ai bấm phát hành HĐĐT tại quầy (M-167): `auto` = popup tự gửi sau khi
     * "Bán ngay" có tích hóa đơn, `manual` = thu ngân xem nháp rồi xác nhận.
     */
    publishTrigger?: 'auto' | 'manual'
    /**
     * Số mặt hàng (sản phẩm + vật tư) kho không đủ khi popup xuất kho mở, đúng
     * các dòng khung cảnh báo thiếu hàng liệt kê. 0 = đủ hàng, không có cảnh báo.
     */
    shortageLineCount?: number
    /**
     * Popup "Nối sản phẩm" (M-145, `kiotviet_item_link`): SP được chọn từ
     * nhóm gợi ý tương đồng hay từ danh sách/tìm kiếm.
     */
    linkPick?: 'suggested' | 'list'
    /** SP được chọn đang nối mã KiotViet khác (phải xác nhận nối lại). */
    relinkConflict?: boolean
    /** Dòng KiotViet có mã hàng (false = dòng không mã, chỉ nối dòng đó). */
    lineHasCode?: boolean
    /** Số SP gợi ý tương đồng server trả cho dòng đang nối. */
    suggestionCount?: number
    /**
     * Xoá phiếu cân tươi (M-148, `tea_purchase_delete`): phiếu chưa gộp, xoá
     * kèm phiếu khô (nguồn duy nhất) hay chỉ gỡ khỏi phiếu khô còn nguồn khác.
     */
    deleteScope?: 'fresh_only' | 'with_dried' | 'detach_from_dried'
    /**
     * Lưu nháp hóa đơn đã sửa (M-170, `einvoice_draft_edit`): số dòng hàng bị
     * xóa khỏi hóa đơn và tổng hóa đơn sau khi lưu có khác tổng đơn không.
     */
    removedLineCount?: number
    invoiceTotalDiffers?: boolean
}
export interface WorkflowSelection {
    searchId?: string
    candidateRank?: number
    entryMethod?: 'manual' | 'barcode'
}
export interface WorkflowStep extends WorkflowSelection {
    fieldGroup?: FieldGroup
    validationCode?: 'required' | 'invalid' | 'stock' | 'other'
}
export interface WorkflowSearch {
    searchId: string
    entityKind?: 'customer' | 'product' | 'lot'
    queryKind?: 'browse' | 'search'
    queryLength: number
    resultCount: number
    hasMore: boolean
}
export interface WorkflowOptions {
    now?: () => number
    capture?: (event: string, props: Record<string, unknown>) => void
}
export const groups: Array<FieldGroup> = [
    'customer',
    'items',
    'shipping',
    'payment',
]
const actions: Array<WorkflowAction> = [
    'api_failed',
    'validation_failed',
    'customer_selected',
    'product_selected',
    'lot_selected',
    'item_added',
    'item_removed',
    'retry',
    'confirm_requested',
    'confirm_failed',
]
export const count = (value: number) =>
    Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0

export function workflowStepProps(
    action: WorkflowAction,
    metadata: WorkflowStep,
    submitIndex: number,
) {
    if (!actions.includes(action)) return
    const props: Record<string, unknown> = {
        action,
        submit_index: submitIndex,
    }
    if (metadata.fieldGroup && groups.includes(metadata.fieldGroup))
        props.field_group = metadata.fieldGroup
    if (
        metadata.validationCode &&
        ['required', 'invalid', 'stock', 'other'].includes(
            metadata.validationCode,
        )
    )
        props.validation_code = metadata.validationCode
    return { ...props, ...workflowSelectionProps(metadata) }
}

export function workflowSearchProps(metadata: WorkflowSearch) {
    const length = count(metadata.queryLength)
    return {
        ...(validSearchId(metadata.searchId)
            ? { search_id: metadata.searchId }
            : {}),
        ...(['customer', 'product', 'lot'].includes(metadata.entityKind ?? '')
            ? {
                  entity_kind: metadata.entityKind,
                  tracking_revision: 'targeted_1',
              }
            : {}),
        query_kind: metadata.queryKind === 'browse' ? 'browse' : 'search',
        query_length_bucket:
            length === 0
                ? 'empty'
                : length <= 5
                  ? '1-5'
                  : length <= 20
                    ? '6-20'
                    : '21+',
        result_count: count(metadata.resultCount),
        has_more: metadata.hasMore === true,
    }
}

const validSearchId = (value: unknown) =>
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        value,
    )

export function workflowSelectionProps(metadata: WorkflowSelection) {
    const props: Record<string, unknown> = {}
    if (validSearchId(metadata.searchId)) props.search_id = metadata.searchId
    if (Number.isInteger(metadata.candidateRank) && metadata.candidateRank! > 0)
        props.candidate_rank = metadata.candidateRank
    if (metadata.entryMethod === 'manual' || metadata.entryMethod === 'barcode')
        props.entry_method = metadata.entryMethod
    if (Object.keys(props).length > 0) props.tracking_revision = 'targeted_1'
    return props
}

// countBucket gom số đếm về 0/1/2-5/6+; giá trị không hợp lệ trả undefined để
// dimension vắng mặt thay vì bịa số.
export function countBucket(value: number | undefined) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0)
        return undefined
    const n = count(value)
    return n === 0 ? '0' : n === 1 ? '1' : n <= 5 ? '2-5' : '6+'
}

export function workflowContextProps(metadata: WorkflowContext) {
    const props: Record<string, unknown> = { tracking_revision: 'targeted_1' }
    const lines = countBucket(metadata.lineCount)
    if (lines) props.line_count_bucket = lines
    const areas = countBucket(metadata.areaCount)
    if (areas) props.area_count_bucket = areas
    const shortage = countBucket(metadata.shortageLineCount)
    if (shortage) props.shortage_line_bucket = shortage
    const suggestions = countBucket(metadata.suggestionCount)
    if (suggestions) props.suggestion_count_bucket = suggestions
    const business = countBucket(metadata.businessLineCount)
    if (business) props.business_line_bucket = business
    if (
        ['unselected', 'prefilled', 'selected'].includes(
            metadata.customerLookupState ?? '',
        )
    )
        props.customer_lookup_state = metadata.customerLookupState
    if (metadata.entryMethod === 'manual' || metadata.entryMethod === 'barcode')
        props.entry_method = metadata.entryMethod
    if (
        metadata.editedField === 'tea_pattern' ||
        metadata.editedField === 'tea_type_id'
    )
        props.edited_field = metadata.editedField
    if (
        metadata.purchaseSource === 'manual' ||
        metadata.purchaseSource === 'smartlab'
    )
        props.purchase_source = metadata.purchaseSource
    if (typeof metadata.hadValue === 'boolean')
        props.had_value = metadata.hadValue
    if (
        metadata.expenseSource === 'manual' ||
        metadata.expenseSource === 'bank_statement'
    )
        props.expense_source = metadata.expenseSource
    if (
        metadata.orderSource === 'counter' ||
        metadata.orderSource === 'kiotviet' ||
        metadata.orderSource === 'standard'
    )
        props.order_source = metadata.orderSource
    if (
        metadata.orderStage === 'awaiting_stock_out' ||
        metadata.orderStage === 'delivered'
    )
        props.order_stage = metadata.orderStage
    if (
        metadata.publishTrigger === 'auto' ||
        metadata.publishTrigger === 'manual'
    )
        props.publish_trigger = metadata.publishTrigger
    if (metadata.linkPick === 'suggested' || metadata.linkPick === 'list')
        props.link_pick = metadata.linkPick
    if (typeof metadata.relinkConflict === 'boolean')
        props.relink_conflict = metadata.relinkConflict
    if (typeof metadata.lineHasCode === 'boolean')
        props.line_has_code = metadata.lineHasCode
    if (
        metadata.deleteScope === 'fresh_only' ||
        metadata.deleteScope === 'with_dried' ||
        metadata.deleteScope === 'detach_from_dried'
    )
        props.delete_scope = metadata.deleteScope
    const removed = countBucket(metadata.removedLineCount)
    if (removed) props.removed_line_bucket = removed
    if (typeof metadata.invoiceTotalDiffers === 'boolean')
        props.invoice_total_differs = metadata.invoiceTotalDiffers
    return { ...props, ...paymentCollectionContextProps(metadata) }
}

/** Optional child contract; children never create attempts. */
export interface WorkflowSearchTracker {
    search: (metadata: WorkflowSearch) => void
    step: (
        action: 'product_selected' | 'lot_selected',
        metadata?: WorkflowSelection,
    ) => void
    setContext: (metadata: WorkflowContext) => void
}
